import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  IconButton,
  Typography as MuiTypography,
  Tooltip,
} from '@mui/material';
import {
  Tag,
  Input,
  Modal,
  Button,
  Avatar,
  Row,
  Col,
  Image,
  Dropdown,
} from 'antd';
import {
  DirectionsCarOutlined as CarIcon,
  VisibilityOutlined as ViewIcon,
  DeleteOutlined as DeleteIcon,
  SearchOutlined as SearchIcon,
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
  EmailOutlined as EmailIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOffOutlined as RejectIcon,
  HourglassEmptyOutlined as PendingIcon,
  RefreshOutlined as RefreshIcon,
  CalendarMonthOutlined as CalendarIcon,
  PublicOutlined as GlobeIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import moment from 'moment';

import {
  fetchDriverStats,
  fetchDrivers,
  updateDriverStatusAsync,
  deleteDriverAsync,
} from '../redux/driverSlice';
import DataTable from '../components/common/DataTable';
import PageHeader from '../components/common/PageHeader';
import DeleteConfirmDialog from '../components/common/DeleteConfirmDialog';

const DriversPage = () => {
  const dispatch = useDispatch();
  const { drivers, stats, status } = useSelector((state) => state.driver);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  const loadData = () => {
    const params = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    dispatch(fetchDrivers(params));
    dispatch(fetchDriverStats());
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadData();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const resultAction = await dispatch(updateDriverStatusAsync({ id, status: newStatus }));
      if (updateDriverStatusAsync.fulfilled.match(resultAction)) {
        toast.success(`Driver status updated to ${newStatus.toUpperCase()}`);
        dispatch(fetchDriverStats());
        setSelectedDriver((prev) => (prev && prev.id === id ? { ...prev, status: newStatus } : prev));
      } else {
        toast.error(resultAction.payload || 'Failed to update driver status');
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
    }
  };

  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!driverToDelete) return;
    setDeleteLoading(true);
    try {
      const resultAction = await dispatch(deleteDriverAsync(driverToDelete.id));
      if (deleteDriverAsync.fulfilled.match(resultAction)) {
        toast.success('Driver registration deleted successfully');
        dispatch(fetchDriverStats());
        setDeleteDialogOpen(false);
        setDriverToDelete(null);
      } else {
        toast.error(resultAction.payload || 'Failed to delete driver');
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewDetails = (driver) => {
    setSelectedDriver(driver);
    setDetailModalVisible(true);
  };

  const getStatusTag = (driverStatus) => {
    const s = (driverStatus || 'pending').toLowerCase();
    switch (s) {
      case 'approved':
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              borderRadius: '20px',
              bgcolor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#047857',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
            <span>APPROVED</span>
          </Box>
        );
      case 'rejected':
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              borderRadius: '20px',
              bgcolor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', boxShadow: '0 0 6px #ef4444' }} />
            <span>REJECTED</span>
          </Box>
        );
      case 'pending':
      default:
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              borderRadius: '20px',
              bgcolor: '#fef3c7',
              border: '1px solid #fde68a',
              color: '#b45309',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', boxShadow: '0 0 6px #f59e0b' }} />
            <span>PENDING</span>
          </Box>
        );
    }
  };

  const columns = [
    {
      id: 'photo',
      label: 'DRIVER',
      width: '240px',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={row.profilePhoto || row.profile_photo}
            icon={<PersonIcon sx={{ color: '#64748b' }} />}
            size={44}
            style={{ border: '2px solid #e2e8f0', backgroundColor: '#f1f5f9' }}
          />
          <Box>
            <MuiTypography sx={{ fontWeight: 600, color: 'var(--header-title)', fontSize: '0.95rem' }}>
              {row.fullName || row.full_name}
            </MuiTypography>
            <MuiTypography variant="caption" sx={{ color: 'var(--subtitle-text)' }}>
              {row.nationality} • {row.gender}
            </MuiTypography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'contact',
      label: 'CONTACT',
      width: '240px',
      render: (row) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            <a
              href={`mailto:${row.email}`}
              style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.85rem' }}
            >
              {row.email}
            </a>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            <MuiTypography variant="caption" sx={{ color: 'var(--header-title)' }}>
              {row.phoneNumber || row.phone_number}
            </MuiTypography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'sport',
      label: 'ASN & PARTICIPATION',
      width: '200px',
      render: (row) => {
        const asn = row.asnName || row.asn_name || 'N/A';
        const country = row.asnCountry || row.asn_country || '';
        const interested = row.interestedInAagc || row.interested_in_aagc;
        return (
          <Box>
            <MuiTypography variant="body2" sx={{ fontWeight: 500, color: 'var(--header-title)' }}>
              {asn} {country ? `(${country})` : ''}
            </MuiTypography>
            <MuiTypography variant="caption" sx={{ color: interested ? '#10b981' : '#94a3b8' }}>
              {interested ? '✓ Interested in AAGC' : 'No AAGC interest'}
            </MuiTypography>
          </Box>
        );
      },
    },
    {
      id: 'history',
      label: 'MOTORSPORT HISTORY',
      width: '180px',
      align: 'center',
      render: (row) => {
        const hasHist = row.hasMotorsportHistory || row.has_motorsport_history;
        return (
          <Tag color={hasHist ? 'cyan' : 'default'}>
            {hasHist ? 'EXPERIENCED' : 'BEGINNER / NO'}
          </Tag>
        );
      },
    },
    {
      id: 'status',
      label: 'STATUS',
      width: '140px',
      align: 'center',
      render: (row) => {
        const s = (row.status || 'pending').toLowerCase();
        const config = {
          pending: {
            bg: '#fef3c7',
            border: '#fde68a',
            color: '#b45309',
            dot: '#f59e0b',
            label: 'PENDING',
          },
          approved: {
            bg: '#ecfdf5',
            border: '#a7f3d0',
            color: '#047857',
            dot: '#10b981',
            label: 'APPROVED',
          },
          rejected: {
            bg: '#fef2f2',
            border: '#fecaca',
            color: '#b91c1c',
            dot: '#ef4444',
            label: 'REJECTED',
          },
        }[s] || {
          bg: '#fef3c7',
          border: '#fde68a',
          color: '#b45309',
          dot: '#f59e0b',
          label: 'PENDING',
        };

        const menuItems = [
          {
            key: 'pending',
            label: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, px: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#b45309' }}>PENDING</span>
              </Box>
            ),
          },
          {
            key: 'approved',
            label: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, px: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10b981' }} />
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#047857' }}>APPROVED</span>
              </Box>
            ),
          },
          {
            key: 'rejected',
            label: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, px: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#b91c1c' }}>REJECTED</span>
              </Box>
            ),
          },
        ];

        return (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: ({ key }) => handleStatusChange(row.id, key),
            }}
            trigger={['click']}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.8,
                px: 1.4,
                py: 0.45,
                borderRadius: '20px',
                bgcolor: config.bg,
                border: `1px solid ${config.border}`,
                color: config.color,
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                cursor: 'pointer',
                userSelect: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                transition: 'all 0.15s ease',
                '&:hover': {
                  opacity: 0.88,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: config.dot,
                  boxShadow: `0 0 6px ${config.dot}`,
                }}
              />
              <span>{config.label}</span>
              <ExpandMoreIcon sx={{ fontSize: 15, opacity: 0.7, ml: -0.2 }} />
            </Box>
          </Dropdown>
        );
      },
    },
    {
      id: 'createdAt',
      label: 'APPLIED ON',
      width: '130px',
      render: (row) => (
        <MuiTypography variant="caption" sx={{ color: 'var(--subtitle-text)', fontWeight: 500 }}>
          {moment(row.createdAt || row.created_at).format('MMM DD, YYYY')}
        </MuiTypography>
      ),
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: '120px',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="View Full Details">
            <IconButton
              onClick={() => handleViewDetails(row)}
              size="small"
              sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' } }}
            >
              <ViewIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Registration">
            <IconButton
              onClick={() => handleDeleteClick(row)}
              size="small"
              sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box className="premium-container">
      <PageHeader
        title="Driver Registrations"
        subtitle="Review, approve, and manage driver submissions from the official championship registration portal."
        titleIcon={<CarIcon />}
      />

      {/* Stats Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#ffffff',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                borderColor: '#cbd5e1',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                bgcolor: '#3b82f6',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <MuiTypography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Total Registrations
                </MuiTypography>
                <MuiTypography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mt: 1, fontSize: '2rem', lineHeight: 1 }}>
                  {stats?.total ?? drivers.length}
                </MuiTypography>
                <MuiTypography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block', fontSize: '0.75rem' }}>
                  All time submissions
                </MuiTypography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #dbeafe',
                }}
              >
                <CarIcon sx={{ fontSize: 26 }} />
              </Box>
            </Box>
          </Box>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#ffffff',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                borderColor: '#cbd5e1',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                bgcolor: '#f59e0b',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <MuiTypography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Pending Review
                </MuiTypography>
                <MuiTypography variant="h3" sx={{ fontWeight: 800, color: '#d97706', mt: 1, fontSize: '2rem', lineHeight: 1 }}>
                  {stats?.pending ?? 0}
                </MuiTypography>
                <MuiTypography variant="caption" sx={{ color: '#d97706', mt: 1, display: 'block', fontSize: '0.75rem', fontWeight: 500 }}>
                  Requires admin action
                </MuiTypography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: '#fffbeb',
                  color: '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #fef3c7',
                }}
              >
                <PendingIcon sx={{ fontSize: 26 }} />
              </Box>
            </Box>
          </Box>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#ffffff',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                borderColor: '#cbd5e1',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                bgcolor: '#10b981',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <MuiTypography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Approved Drivers
                </MuiTypography>
                <MuiTypography variant="h3" sx={{ fontWeight: 800, color: '#059669', mt: 1, fontSize: '2rem', lineHeight: 1 }}>
                  {stats?.approved ?? 0}
                </MuiTypography>
                <MuiTypography variant="caption" sx={{ color: '#059669', mt: 1, display: 'block', fontSize: '0.75rem', fontWeight: 500 }}>
                  Confirmed competitors
                </MuiTypography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: '#ecfdf5',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #d1fae5',
                }}
              >
                <ApproveIcon sx={{ fontSize: 26 }} />
              </Box>
            </Box>
          </Box>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#ffffff',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                borderColor: '#cbd5e1',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                bgcolor: '#ef4444',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <MuiTypography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Rejected Submissions
                </MuiTypography>
                <MuiTypography variant="h3" sx={{ fontWeight: 800, color: '#dc2626', mt: 1, fontSize: '2rem', lineHeight: 1 }}>
                  {stats?.rejected ?? 0}
                </MuiTypography>
                <MuiTypography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block', fontSize: '0.75rem' }}>
                  Declined applications
                </MuiTypography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: '#fef2f2',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #fee2e2',
                }}
              >
                <RejectIcon sx={{ fontSize: 26 }} />
              </Box>
            </Box>
          </Box>
        </Col>
      </Row>

      {/* Filter and Search Bar Toolbar */}
      <Box
        sx={{
          mb: 3,
          p: '14px 18px',
          bgcolor: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Search Input and Button */}
        <form
          onSubmit={handleSearch}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: '1 1 360px',
            maxWidth: 520,
          }}
        >
          <Input
            size="large"
            placeholder="Search by driver name, email or nationality..."
            prefix={<SearchIcon sx={{ color: '#94a3b8', fontSize: 20, mr: 0.5 }} />}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (!e.target.value) {
                const params = {};
                if (statusFilter !== 'all') params.status = statusFilter;
                dispatch(fetchDrivers(params));
                dispatch(fetchDriverStats());
              }
            }}
            allowClear
            style={{
              height: 40,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              fontSize: '0.875rem',
            }}
          />
          <Button
            size="large"
            htmlType="submit"
            style={{
              height: 40,
              borderRadius: 8,
              backgroundColor: '#0f172a',
              borderColor: '#0f172a',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.875rem',
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(15, 23, 42, 0.12)',
              cursor: 'pointer',
            }}
          >
            Search
          </Button>
        </form>

        {/* Status Filter Pills & Refresh */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Status Tabs */}
          <Box
            sx={{
              display: 'inline-flex',
              bgcolor: '#f1f5f9',
              p: '3px',
              borderRadius: '9px',
              gap: '2px',
            }}
          >
            {[
              { key: 'all', label: 'All', count: stats?.total ?? drivers.length },
              { key: 'pending', label: 'Pending', count: stats?.pending ?? 0, color: '#d97706' },
              { key: 'approved', label: 'Approved', count: stats?.approved ?? 0, color: '#059669' },
              { key: 'rejected', label: 'Rejected', count: stats?.rejected ?? 0, color: '#dc2626' },
            ].map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  sx={{
                    px: 1.5,
                    py: 0.65,
                    borderRadius: '7px',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#0f172a' : '#64748b',
                    bgcolor: isActive ? '#ffffff' : 'transparent',
                    boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    userSelect: 'none',
                    '&:hover': {
                      color: '#0f172a',
                    },
                  }}
                >
                  <span>{tab.label}</span>
                  <Box
                    component="span"
                    sx={{
                      px: 0.7,
                      py: 0.1,
                      borderRadius: '8px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      bgcolor: isActive ? (tab.color ? `${tab.color}15` : '#f1f5f9') : '#e2e8f0',
                      color: tab.color || '#64748b',
                    }}
                  >
                    {tab.count}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Refresh Action Button */}
          <Tooltip title="Refresh registration list">
            <Button
              size="large"
              onClick={loadData}
              icon={<RefreshIcon sx={{ fontSize: 18, color: '#64748b' }} />}
              style={{
                height: 40,
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                color: '#334155',
                fontWeight: 600,
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#ffffff',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                padding: '0 16px',
              }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Data Table */}
      <Box className="premium-table-card">
        <DataTable
          premium={true}
          columns={columns}
          data={drivers}
          loading={status === 'loading'}
          emptyTitle="No Driver Registrations Found"
          emptySubtitle="No registration submissions match your current filter criteria."
        />
      </Box>

      {/* View Driver Details Modal */}
      <Modal
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 4, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={selectedDriver?.profilePhoto || selectedDriver?.profile_photo}
                icon={<PersonIcon />}
                size={40}
                style={{ backgroundColor: '#fbbf24', color: '#000', fontWeight: 700 }}
              />
              <Box>
                <MuiTypography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'var(--header-title)' }}>
                  {selectedDriver?.fullName || selectedDriver?.full_name}
                </MuiTypography>
                <MuiTypography variant="caption" sx={{ color: '#64748b' }}>
                  {selectedDriver?.createdAt || selectedDriver?.created_at
                    ? `Registered on ${moment(selectedDriver.createdAt || selectedDriver.created_at).format('MMM DD, YYYY [at] hh:mm A')}`
                    : 'Driver Registration Application'}
                </MuiTypography>
              </Box>
            </Box>
            <Box sx={{ mr: 2 }}>{selectedDriver && getStatusTag(selectedDriver.status)}</Box>
          </Box>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={740}
        centered
        styles={{
          body: {
            maxHeight: '75vh',
            overflowY: 'auto',
            padding: '16px 20px',
          },
        }}
        footer={[
          <Button
            key="close"
            size="middle"
            onClick={() => setDetailModalVisible(false)}
            style={{
              height: 38,
              padding: '0 18px',
              borderRadius: 8,
              fontWeight: 500,
              color: '#475569',
              borderColor: '#cbd5e1',
              backgroundColor: '#ffffff',
            }}
          >
            Close
          </Button>,
          (selectedDriver?.status === 'approved' || selectedDriver?.status === 'rejected') && (
            <Button
              key="pending"
              size="middle"
              icon={<PendingIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4, color: '#d97706' }} />}
              onClick={() => {
                handleStatusChange(selectedDriver.id, 'pending');
              }}
              style={{
                height: 38,
                padding: '0 16px',
                borderRadius: 8,
                fontWeight: 600,
                color: '#d97706',
                borderColor: '#fcd34d',
                backgroundColor: '#fefce8',
              }}
            >
              Reset to Pending
            </Button>
          ),
          <Button
            key="reject"
            size="middle"
            disabled={selectedDriver?.status === 'rejected'}
            icon={<RejectIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />}
            onClick={() => {
              handleStatusChange(selectedDriver.id, 'rejected');
            }}
            style={{
              height: 38,
              padding: '0 18px',
              borderRadius: 8,
              fontWeight: 600,
              backgroundColor: selectedDriver?.status === 'rejected' ? '#f1f5f9' : '#fee2e2',
              borderColor: selectedDriver?.status === 'rejected' ? '#e2e8f0' : '#fca5a5',
              color: selectedDriver?.status === 'rejected' ? '#94a3b8' : '#dc2626',
              cursor: selectedDriver?.status === 'rejected' ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {selectedDriver?.status === 'rejected' ? 'Rejected' : 'Reject Driver'}
          </Button>,
          <Button
            key="approve"
            size="middle"
            disabled={selectedDriver?.status === 'approved'}
            icon={<ApproveIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4, color: selectedDriver?.status === 'approved' ? '#94a3b8' : '#ffffff' }} />}
            style={{
              height: 38,
              padding: '0 20px',
              borderRadius: 8,
              fontWeight: 600,
              backgroundColor: selectedDriver?.status === 'approved' ? '#e2e8f0' : '#10b981',
              borderColor: selectedDriver?.status === 'approved' ? '#cbd5e1' : '#10b981',
              color: selectedDriver?.status === 'approved' ? '#94a3b8' : '#ffffff',
              boxShadow: selectedDriver?.status === 'approved' ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.35)',
              cursor: selectedDriver?.status === 'approved' ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
            }}
            onClick={() => {
              handleStatusChange(selectedDriver.id, 'approved');
            }}
          >
            {selectedDriver?.status === 'approved' ? 'Approved ✓' : 'Approve Driver'}
          </Button>,
        ].filter(Boolean)}
      >
        {selectedDriver && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
            {/* Top Profile Card Banner */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '14px',
                p: { xs: 2, sm: 2.5 },
                color: '#ffffff',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                gap: 2.5,
                position: 'relative',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.15)',
              }}
            >
              {/* Photo preview */}
              <Box sx={{ flexShrink: 0 }}>
                {selectedDriver.profilePhoto || selectedDriver.profile_photo ? (
                  <Image
                    src={selectedDriver.profilePhoto || selectedDriver.profile_photo}
                    alt={selectedDriver.fullName || selectedDriver.full_name}
                    width={96}
                    height={96}
                    style={{
                      borderRadius: 12,
                      objectFit: 'cover',
                      border: '2px solid rgba(251, 191, 36, 0.6)',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: '12px',
                      bgcolor: 'rgba(251, 191, 36, 0.15)',
                      border: '2px dashed rgba(251, 191, 36, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fbbf24',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 44 }} />
                  </Box>
                )}
              </Box>

              {/* Driver Summary Details */}
              <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
                <MuiTypography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#ffffff',
                    lineHeight: 1.2,
                    mb: 1,
                  }}
                >
                  {selectedDriver.fullName || selectedDriver.full_name}
                </MuiTypography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  {/* Nationality Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: '#e2e8f0',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                    }}
                  >
                    <GlobeIcon sx={{ fontSize: 15, color: '#fbbf24' }} />
                    <span>{selectedDriver.nationality || 'Nationality N/A'}</span>
                  </Box>

                  {/* Gender Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: '#e2e8f0',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 15, color: '#38bdf8' }} />
                    <span>{selectedDriver.gender || 'Gender N/A'}</span>
                  </Box>

                  {/* DOB Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: '#e2e8f0',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 15, color: '#a78bfa' }} />
                    <span>
                      {moment(selectedDriver.dateOfBirth || selectedDriver.date_of_birth).isValid()
                        ? moment(selectedDriver.dateOfBirth || selectedDriver.date_of_birth).format('MMM DD, YYYY')
                        : (selectedDriver.dateOfBirth || selectedDriver.date_of_birth || 'DOB N/A')}
                    </span>
                  </Box>
                </Box>

                {/* Quick Status Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    gap: 1.2,
                  }}
                >
                  <MuiTypography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Status:
                  </MuiTypography>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'pending',
                          label: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <PendingIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                              <span style={{ fontWeight: 600, color: '#d97706' }}>Pending</span>
                            </Box>
                          ),
                        },
                        {
                          key: 'approved',
                          label: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <ApproveIcon sx={{ fontSize: 16, color: '#10b981' }} />
                              <span style={{ fontWeight: 600, color: '#059669' }}>Approved</span>
                            </Box>
                          ),
                        },
                        {
                          key: 'rejected',
                          label: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <RejectIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                              <span style={{ fontWeight: 600, color: '#dc2626' }}>Rejected</span>
                            </Box>
                          ),
                        },
                      ],
                      onClick: ({ key }) => handleStatusChange(selectedDriver.id, key),
                    }}
                    trigger={['click']}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.8,
                        px: 1.5,
                        py: 0.45,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s ease',
                        ...((selectedDriver.status || 'pending').toLowerCase() === 'approved'
                          ? {
                              bgcolor: 'rgba(16, 185, 129, 0.15)',
                              border: '1px solid rgba(16, 185, 129, 0.45)',
                              color: '#34d399',
                              '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.25)' },
                            }
                          : (selectedDriver.status || 'pending').toLowerCase() === 'rejected'
                          ? {
                              bgcolor: 'rgba(239, 68, 68, 0.15)',
                              border: '1px solid rgba(239, 68, 68, 0.45)',
                              color: '#f87171',
                              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.25)' },
                            }
                          : {
                              bgcolor: 'rgba(245, 158, 11, 0.15)',
                              border: '1px solid rgba(245, 158, 11, 0.45)',
                              color: '#fbbf24',
                              '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.25)' },
                            }),
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor:
                            (selectedDriver.status || 'pending').toLowerCase() === 'approved'
                              ? '#10b981'
                              : (selectedDriver.status || 'pending').toLowerCase() === 'rejected'
                              ? '#ef4444'
                              : '#f59e0b',
                          boxShadow:
                            (selectedDriver.status || 'pending').toLowerCase() === 'approved'
                              ? '0 0 8px #10b981'
                              : (selectedDriver.status || 'pending').toLowerCase() === 'rejected'
                              ? '0 0 8px #ef4444'
                              : '0 0 8px #f59e0b',
                        }}
                      />
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em' }}>
                        {(selectedDriver.status || 'pending').toUpperCase()}
                      </span>
                      <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    </Box>
                  </Dropdown>
                </Box>
              </Box>
            </Box>

            {/* Grid of Details Sections */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* Card 1: Contact Information */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                  <EmailIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                  <MuiTypography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.05em', color: '#475569' }}>
                    CONTACT INFORMATION
                  </MuiTypography>
                </Box>

                <Box>
                  <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                    Email Address
                  </MuiTypography>
                  <a
                    href={`mailto:${selectedDriver.email}`}
                    style={{
                      color: '#2563eb',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      wordBreak: 'break-all',
                    }}
                  >
                    {selectedDriver.email}
                  </a>
                </Box>

                <Box>
                  <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                    Phone Number
                  </MuiTypography>
                  <a
                    href={`tel:${selectedDriver.phoneNumber || selectedDriver.phone_number}`}
                    style={{
                      color: '#0f172a',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                    }}
                  >
                    {selectedDriver.phoneNumber || selectedDriver.phone_number || 'N/A'}
                  </a>
                </Box>

                <Box>
                  <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                    Social Media / Handle
                  </MuiTypography>
                  <MuiTypography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: (selectedDriver.socialMedia || selectedDriver.social_media) ? '#0f172a' : '#94a3b8',
                      fontStyle: (selectedDriver.socialMedia || selectedDriver.social_media) ? 'normal' : 'italic',
                    }}
                  >
                    {selectedDriver.socialMedia || selectedDriver.social_media || 'None provided'}
                  </MuiTypography>
                </Box>
              </Box>

              {/* Card 2: Motorsport & Participation */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                  <CarIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                  <MuiTypography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.05em', color: '#475569' }}>
                    SPORT & PARTICIPATION
                  </MuiTypography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                      ASN Club / Name
                    </MuiTypography>
                    <MuiTypography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {selectedDriver.asnName || selectedDriver.asn_name || 'N/A'}
                    </MuiTypography>
                  </Box>

                  <Box>
                    <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                      ASN Country
                    </MuiTypography>
                    <MuiTypography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {selectedDriver.asnCountry || selectedDriver.asn_country || 'N/A'}
                    </MuiTypography>
                  </Box>
                </Box>

                <Box>
                  <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500, mb: 0.5 }}>
                    Interested in Participating in AAGC
                  </MuiTypography>
                  {(selectedDriver.interestedInAagc || selectedDriver.interested_in_aagc) ? (
                    <Tag
                      color="green"
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        margin: 0,
                      }}
                    >
                      ✓ Yes, Interested in AAGC
                    </Tag>
                  ) : (
                    <Tag
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        margin: 0,
                        color: '#64748b',
                      }}
                    >
                      No Interest Indicated
                    </Tag>
                  )}
                </Box>

                <Box>
                  <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500, mb: 0.5 }}>
                    Past Motorsport History
                  </MuiTypography>
                  {(selectedDriver.hasMotorsportHistory || selectedDriver.has_motorsport_history) ? (
                    <Tag
                      color="cyan"
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        margin: 0,
                      }}
                    >
                      🏎️ Experienced Competitor
                    </Tag>
                  ) : (
                    <Tag
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        margin: 0,
                        color: '#64748b',
                      }}
                    >
                      No Prior Motorsport History
                    </Tag>
                  )}
                </Box>

                {(selectedDriver.historyDescription || selectedDriver.history_description) && (
                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <MuiTypography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 600, mb: 0.5 }}>
                      Experience Description
                    </MuiTypography>
                    <MuiTypography variant="body2" sx={{ color: '#334155', fontStyle: 'italic', fontSize: '0.82rem' }}>
                      "{selectedDriver.historyDescription || selectedDriver.history_description}"
                    </MuiTypography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Modal>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={`driver registration for ${driverToDelete?.fullName || driverToDelete?.full_name || 'this driver'}`}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default DriversPage;
